import React, { useMemo } from "react";
import { Button, Spin, Tooltip, message } from "antd";
import { useTranslation } from "react-i18next";

import { SchoolsProvider, useSchools } from "./store";
import FiltersToolbar from "./FiltersToolbar";
import Legend from "./Legend";
import Section from "./Section";
import StatisticsPanel from "./StatisticsPanel";
import PieCharts from "./PieCharts";
import BarChart from "./BarChart";
import SummaryTable from "./SummaryTable";
import EmptyState from "./EmptyState";

import { downloadExport } from "./api";
import { pickLabel, exportLocale } from "./helpers";
import "./styles.css";

function Screen() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const {
    meta,
    data,
    filters,
    loading,
    error,
    setFilter,
    toggleFilter,
    resetFilters,
    reload,
  } = useSchools();

  const statusLabels = useMemo(() => {
    const src = meta?.statuses || [];
    return src.reduce((acc, o) => {
      acc[o.key] = pickLabel(o, lng);
      return acc;
    }, {});
  }, [meta, lng]);

  const filterCaption = useMemo(() => {
    if (!meta) return "";
    const META_BY_FIELD = {
      shift: meta.shifts,
      lang: meta.languages,
      status: meta.statuses,
    };
    const labelOf = (field, value) => {
      const found = (META_BY_FIELD[field] || []).find((o) => o.key === value);
      return found ? pickLabel(found, lng) : value;
    };
    const parts = [];
    if (filters.shift)
      parts.push(
        `${t("schools.filterShift")}: ${labelOf("shift", filters.shift)}`,
      );
    if (filters.lang)
      parts.push(
        `${t("schools.filterLang")}: ${labelOf("lang", filters.lang)}`,
      );
    if (filters.status)
      parts.push(
        `${t("schools.filterStatus")}: ${labelOf("status", filters.status)}`,
      );
    return `· ${t("schools.filterCaption")}: ${
      parts.length ? parts.join(" · ") : t("schools.filterNone")
    }`;
  }, [meta, filters, lng, t]);

  const items = data?.items || [];
  const isEmpty = !loading && !error && items.length === 0;

  const doExport = async (format) => {
    try {
      await downloadExport(format, {
        ...filters,
        locale: exportLocale(lng),
      });
    } catch (e) {
      message.error(t("schools.exportError"));
    }
  };

  const exportActions = (
    <>
      <span className="sch-ctrl-label">{t("schools.export")}</span>
      <Tooltip title={isEmpty ? t("schools.expEmpty") : t("schools.expHint")}>
        <span>
          <Button
            className="sch-exp"
            disabled={isEmpty}
            onClick={() => doExport("xlsx")}
          >
            {t("schools.expXlsx")}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={isEmpty ? t("schools.expEmpty") : t("schools.expHint")}>
        <span>
          <Button
            className="sch-exp"
            disabled={isEmpty}
            onClick={() => doExport("pdf")}
          >
            {t("schools.expPdf")}
          </Button>
        </span>
      </Tooltip>
    </>
  );

  return (
    <div className="sch-page">
      <FiltersToolbar
        meta={meta}
        filters={filters}
        onChange={setFilter}
        onReset={resetFilters}
      />
      <Legend />

      {error ? (
        <div className="sch-loaderror">
          <span>{t("schools.loadError")}</span>
          <Button size="small" onClick={reload}>
            {t("schools.retry")}
          </Button>
        </div>
      ) : (
        <Spin spinning={loading}>
          <Section title={t("schools.sectionSummary")} caption={filterCaption}>
            <StatisticsPanel summary={data?.summary} />
          </Section>

          <Section title={t("schools.sectionCuts")} caption={filterCaption}>
            {isEmpty ? (
              <EmptyState />
            ) : (
              <PieCharts
                meta={meta}
                cuts={data?.cuts}
                filters={filters}
                onToggle={toggleFilter}
              />
            )}
          </Section>

          <Section title={t("schools.sectionChart")} caption={filterCaption}>
            {isEmpty ? (
              <EmptyState />
            ) : (
              <BarChart items={items} statusLabels={statusLabels} />
            )}
          </Section>

          <Section
            title={t("schools.sectionTable")}
            caption={filterCaption}
            actions={exportActions}
          >
            {isEmpty ? (
              <EmptyState />
            ) : (
              <SummaryTable items={items} statusLabels={statusLabels} />
            )}
          </Section>
        </Spin>
      )}
    </div>
  );
}

export default function SchoolsPage() {
  return (
    <SchoolsProvider>
      <Screen />
    </SchoolsProvider>
  );
}
